import React from 'react';

import { Box, Flex, Image } from '@chakra-ui/react';
import { usePartialPublishEventDetails } from 'screens/performanceReview/helpers';
import { addPluralSuffixS } from 'utils/helpers/stringHelpers';

import NotifyDone from 'assets/assetsV1/illustrations/newIllustrations/notifyDone.svg';

import { REVIEW_PROVIDER_TYPES, REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP_1 } from 'constants/ProjectConstants';
import {
  sendBulkNotifyClickNotifyEvent,
  sendBulkNotifyCloseReviewsEvent,
  sendBulkNotifyReviewerTypeCheckboxEvent,
} from 'constants/mixpanelHelpers/bulkNotifyEvents';

import { DrawerBody, DrawerFooter, DrawerHeaderSecondary } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { NestedCheckbox } from 'uiToolkitV1/NestedCheckbox';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useBulkNotifyContext } from '../BulkNotifyContext';
import { useGetBulkNotifyOptionsQuery, useSubmitBulkNotifyMutation } from '../dataHooks';

const Note = () => (
  <Box
    borderRadius="4px"
    border="1px solid"
    borderColor="border.EBEFF3"
    px={4}
    py={3}
    mt={5}
    background="surface.FDFDFD"
  >
    <TextLabel>
      Note : Notifications will be sent to pending reviewers, except in cases where due dates have passed, you lack
      permission to notify reviewers of your Dotted Line Reports, or they have already submitted.
    </TextLabel>
  </Box>
);

const NoActionComp = () => (
  <Flex
    flexDir="column"
    bg="radial-gradient(299.07% 219.61% at 50% 50%, rgba(219, 242, 211, 0.00) 0%, #DBF2D3 97.92%)"
    p="30px 16px 40px"
    borderRadius="8px"
    alignItems="center"
    border="1px solid"
    borderColor="border.EBEFF3"
    mt={1}
  >
    <Image src={NotifyDone} maxH="160px" alt="Notifications done image" style={{}} />

    <TextBodyMedium bold mb={2} mt={4}>
      You are all caught up!
    </TextBodyMedium>
    <TextBodyMedium mx="64px" textAlign="center">
      All pending reviewers for selected employees have already been notified. Here are few reasons why the reviewer
      wont be notified:
    </TextBodyMedium>
    <Box mx="56px" mt={3}>
      <ul>
        <li>
          <TextBodyMedium>Due dates have passed</TextBodyMedium>
        </li>
        <li>
          <TextBodyMedium>
            The employee is your dotted line report and you don&apos;t have permission to notify their reviewers
          </TextBodyMedium>
        </li>
        <li>
          <TextBodyMedium>They have already submitted</TextBodyMedium>
        </li>
      </ul>
    </Box>
  </Flex>
);

const PROVIDER_TYPE_TO_TEXT_MAP = {
  [REVIEW_PROVIDER_TYPES.PEER]: 'peer',
  [REVIEW_PROVIDER_TYPES.MANAGER]: 'manager',
  [REVIEW_PROVIDER_TYPES.DLM]: 'dotted line manager',
};

const getProviderCategoryText = ({ providerType, recipientCount, employeeCount }) => {
  if (providerType === REVIEW_PROVIDER_TYPES.SELF) {
    return `Notify ${recipientCount} employee${addPluralSuffixS(recipientCount)}`;
  }
  if (providerType === REVIEW_PROVIDER_TYPES.UPWARD) {
    return `Notify ${recipientCount} team member${addPluralSuffixS(recipientCount)}`;
  }
  return `Notify ${recipientCount} ${PROVIDER_TYPE_TO_TEXT_MAP?.[providerType] ?? ''}${addPluralSuffixS(
    recipientCount
  )} for ${employeeCount} employee${addPluralSuffixS(employeeCount)}`;
};

const NotifyOptions = ({ data }) => {
  const { selectedOptions, setSelectedOptions } = useBulkNotifyContext();

  const options = data.map(({ providerType, employeeCount, recipientCount }) => ({
    key: providerType,
    label: REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP_1[providerType],
    subLabel: getProviderCategoryText({ providerType, recipientCount, employeeCount }),
  }));

  const { page, module } = usePartialPublishEventDetails();

  const handleAllChangeEvent = () => {
    sendBulkNotifyReviewerTypeCheckboxEvent({
      eventProperties: {
        page,
        source: module,
        type: 'All reviewers',
      },
    });
  };

  const handleSubOptionChangeEvent = (changedOptionKey) => {
    sendBulkNotifyReviewerTypeCheckboxEvent({
      eventProperties: {
        page,
        source: module,
        type: changedOptionKey,
      },
    });
  };

  return (
    <NestedCheckbox
      options={options}
      selectedOptionsKeys={selectedOptions}
      onChange={setSelectedOptions}
      allOptionsLabel="All Pending Reviewers"
      allOptionsSubLabel="Select reviewer groups that should be notified"
      onAllChange={handleAllChangeEvent}
      onSubOptionChange={handleSubOptionChangeEvent}
    />
  );
};

const Footer = ({ data, isActionAvailable, isSuccess, onClose, handleNotifyClickWithEvent }) => {
  const { selectedOptions } = useBulkNotifyContext();
  const { page, module } = usePartialPublishEventDetails();

  const onCloseWithEvent = () => {
    sendBulkNotifyCloseReviewsEvent({
      eventProperties: {
        page,
        source: module,
        type: !isActionAvailable ? 'noNotifDone' : 'cancel',
      },
    });
    onClose();
  };

  return (
    <DrawerFooter mt="auto">
      <Button
        w="94px"
        variant={isActionAvailable ? 'outlined' : 'filled'}
        mr={isActionAvailable ? 4 : 0}
        onClick={onCloseWithEvent}
      >
        {isSuccess && data?.length === 0 ? 'Done' : 'Cancel'}
      </Button>

      {isActionAvailable && (
        <Button
          w="94px"
          variant="filled"
          colorScheme="blue"
          onClick={handleNotifyClickWithEvent}
          disabled={!selectedOptions.length}
        >
          Notify
        </Button>
      )}
    </DrawerFooter>
  );
};

const BulkNotifyOptions = ({ onClose }) => {
  const { data, isLoading, isSuccess, isError } = useGetBulkNotifyOptionsQuery();
  const { mutate: bulkNotifyMutation } = useSubmitBulkNotifyMutation();

  const isActionAvailable = isSuccess && data?.length > 0;

  const { module, page } = usePartialPublishEventDetails();

  const handleNotifyClickWithEvent = () => {
    sendBulkNotifyClickNotifyEvent({
      eventProperties: {
        page,
        source: 'Bulk Notify',
      },
    });
    bulkNotifyMutation();
  };

  const onCloseWithEvent = () => {
    sendBulkNotifyCloseReviewsEvent({
      eventProperties: {
        page,
        source: module,
        type: 'cancel',
      },
    });
    onClose();
  };

  return (
    <>
      <DrawerHeaderSecondary onClose={onCloseWithEvent}>
        <Flex
          alignItems="center"
          borderBottom="1px"
          borderBottomColor="neutral.CFD7DF"
          px={4}
          py={5}
          justifyContent="space-between"
        >
          <TextBodyMedium bold>Notify pending reviewers for selected employees</TextBodyMedium>
        </Flex>
      </DrawerHeaderSecondary>

      <DrawerBody maxH="calc(100vh - 200px)">
        <Flex flexDir="column" m={4} mt={3}>
          {isLoading && (
            <Box mb="120px" mt={3}>
              <SkeletonLoader />
            </Box>
          )}
          {isActionAvailable && (
            <>
              <NotifyOptions data={data} />
              <Note />
            </>
          )}
          {(isError || (isSuccess && data?.length === 0)) && <NoActionComp />}
        </Flex>
      </DrawerBody>

      <Footer
        data={data}
        isActionAvailable={isActionAvailable}
        isSuccess={isSuccess}
        handleNotifyClickWithEvent={handleNotifyClickWithEvent}
        onClose={onClose}
      />
    </>
  );
};

export { BulkNotifyOptions };
